import { graphemeLength } from "@/lib/graphemes";
import {
  definesAcronym,
  findAcronyms,
  findCitations,
} from "@/lib/tool-kit/publishing/text-checks";

/**
 * Research highlights — the three to five bullet points many Elsevier journals publish
 * with an article.
 *
 * The presets are the published rules, checked against the publishers' own pages:
 * Elsevier asks for 3–5 highlights of at most 85 characters each, including spaces, and
 * Cell Press for 3–4 of the same length (Cell's final-file requirements). A journal's
 * Guide for Authors overrides both, which is what the custom preset is for.
 */

export type HighlightPresetId = "elsevier" | "cell" | "custom";

export interface HighlightRules {
  minBullets: number;
  maxBullets: number;
  maxCharacters: number;
}

export const HIGHLIGHT_PRESETS: Readonly<
  Record<Exclude<HighlightPresetId, "custom">, HighlightRules>
> = {
  elsevier: { minBullets: 3, maxBullets: 5, maxCharacters: 85 },
  cell: { minBullets: 3, maxBullets: 4, maxCharacters: 85 },
};

/** A bullet, dash, asterisk or "1." / "1)" opening a pasted line. */
const RE_BULLET = /^\s*(?:[-–—•●▪◦*·‣⁃]|\d{1,2}[.)])\s+/u;

/** One highlight per non-empty line, with any bullet marker removed. */
export function parseHighlights(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.replace(RE_BULLET, "").trim())
    .filter((line) => line !== "");
}

export interface AcronymUse {
  acronym: string;
  /** Spelled out in the same highlight. */
  defined: boolean;
}

export interface HighlightCheck {
  text: string;
  /** Characters including spaces, counted as a reader sees them. */
  characters: number;
  /** Negative when over the limit. */
  remaining: number;
  citations: string[];
  acronyms: AcronymUse[];
  /** Repeats an earlier highlight (ignoring case). */
  duplicate: boolean;
}

export function checkHighlights(
  highlights: readonly string[],
  rules: HighlightRules,
  locale?: string
): HighlightCheck[] {
  const seen = new Set<string>();
  return highlights.map((text) => {
    const characters = graphemeLength(text, locale);
    const key = text.toLocaleLowerCase(locale);
    const duplicate = seen.has(key);
    seen.add(key);
    return {
      text,
      characters,
      remaining: rules.maxCharacters - characters,
      citations: findCitations(text),
      acronyms: findAcronyms(text).map((acronym) => ({
        acronym,
        defined: definesAcronym(text, acronym),
      })),
      duplicate,
    };
  });
}

export type CountStatus = "few" | "ok" | "many";

export function countStatus(count: number, rules: HighlightRules): CountStatus {
  if (count < rules.minBullets) {
    return "few";
  }
  return count > rules.maxBullets ? "many" : "ok";
}
